import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollToSection } from "@/components/SmoothScroll";

const EASE = [0.22, 1, 0.36, 1];

const LINES = [
  { text: "Design that sells", accent: false },
  { text: "without saying", accent: false },
  { text: "a word.", accent: true },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-[100svh] flex items-center overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src="https://images.pexels.com/photos/21031387/pexels-photo-21031387.jpeg?auto=compress&cs=tinysrgb&w=1920"
          alt=""
          className="w-full h-full object-cover opacity-30"
          loading="eager"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/40 to-ink" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_40%,rgba(212,175,55,0.12),transparent_55%)]" />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-10 pt-28 pb-24">
        <div className="overflow-hidden mb-8">
          <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 1, delay: 0.45, ease: EASE }} className="h-px w-24 bg-gold origin-left" />
        </div>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
          className="eyebrow mb-8"
        >
          Southline Creative — Independent Design Studio
        </motion.p>

        <h1 className="font-display font-medium text-[15vw] sm:text-7xl lg:text-[7rem] leading-[0.98] tracking-tight">
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.accent ? "italic text-gold" : "text-cream"}`}
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, delay: 0.85 + i * 0.14, ease: EASE }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.45, ease: EASE }}
          className="mt-8 max-w-xl text-base md:text-lg text-cream/60 font-light leading-relaxed"
        >
          Websites, business cards and leaflets for businesses that refuse to
          blend in. Designed with obsession, delivered with intent.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.6, ease: EASE }}
          className="mt-12 flex flex-col sm:flex-row gap-4"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={() => scrollToSection("contact")}
            className="btn-shine group inline-flex items-center justify-center gap-2 bg-gold text-ink px-9 py-4 text-sm uppercase tracking-[0.2em] font-medium hover:bg-gold-soft transition-colors duration-300"
          >
            Start a project
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => scrollToSection("work")}
            className="inline-flex items-center justify-center gap-2 border border-white/20 text-cream px-9 py-4 text-sm uppercase tracking-[0.2em] hover:border-gold hover:text-gold transition-colors duration-300"
          >
            See the work
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.1, duration: 1 }}
        className="absolute bottom-8 left-6 md:left-10 z-10 flex items-center gap-4"
      >
        <div className="w-px h-14 bg-gold/70 animate-scroll-cue" />
        <span className="text-[11px] uppercase tracking-[0.3em] text-cream/40 flex items-center gap-2">
          Scroll <ArrowDown className="w-3 h-3" />
        </span>
      </motion.div>
    </section>
  );
}
